import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import logger from '../util/logger.js';

export function createSensorLabel(sensor, colorHex, opts = {}) {
	const label = document.createElement('div');
	label.className = 'hidden';
	// styling handled in CSS; avoid inline styles where possible

	const uid = (opts && opts.uid) ? String(opts.uid) : (sensor && (sensor.id || sensor.label) ? String(sensor.id || sensor.label) : '');
	label.dataset.sensorUid = uid;

	const inner = document.createElement('div');
	inner.className = 'sensor-card label-inner';
	inner.innerHTML = `<strong>${sensor.label || sensor.id || ''}</strong><div class="val">${sensor.value ?? '—'} ${sensor.unit ?? ''}</div>`;
	inner.style.pointerEvents = 'auto';

	label.appendChild(inner);

	const labelObj = new CSS2DObject(label);
	labelObj.center = new THREE.Vector2(1.0, 0.5);
	labelObj.position.set(0, 0, 0);

	try { label.style.setProperty('--accent', colorHex); } catch (e) { logger.warn && logger.warn('labels: set accent failed', e); }

	return { labelEl: label, labelObj };
}

export function showLabelEl(el, { persistent = false } = {}) {
	if (!el) return;
	el.classList.remove('hidden');
	el.classList.add('visible');
	if (persistent) el.classList.add('persistent');
}

export function hideLabelEl(el, { force = false } = {}) {
	if (!el) return;
	if (!force && el.classList.contains('persistent')) return;
	el.classList.remove('visible');
	el.classList.add('hidden');
}

export function liftLabelEl(el) {
	if (!el) return;
	const inner = el.querySelector('.sensor-card');
	const nodeToLift = inner || el;
	nodeToLift.classList.add('lifted');
	try {
		// Prefer the explicitly attached container, fall back to current parent
		const container = el.__labelContainer || el.parentElement;
		if (container && container.nodeType === 1) container.appendChild(el);
	} catch (e) { logger.warn && logger.warn('labels: DOM reordering failed', e); }
}

export function unliftLabelEl(el) {
	if (!el) return;
	const inner = el.querySelector('.sensor-card');
	const nodeToUnlift = inner || el;
	nodeToUnlift.classList.remove('lifted');
}

createSensorLabel._attachDomHover = function (labelEl) {
	if (!labelEl) return;
	const innerEl = labelEl.children[0] || labelEl;

	// create named handlers so they can be removed later
	const onEnter = (ev) => {
		if (labelEl.__disposed) return;
		try {
			// pointer enter (silent)
			const sensorUid = labelEl.dataset && labelEl.dataset.sensorUid ? labelEl.dataset.sensorUid : (labelEl.dataset && labelEl.dataset.sensorId ? labelEl.dataset.sensorId : null);
			const e = new CustomEvent('label:enter', { detail: { sensorUid }, bubbles: true, composed: true });
			labelEl.dispatchEvent(e);
		} catch (err) { logger.warn && logger.warn('labels: pointerenter handler failed', err); }
	};

	const onLeave = (ev) => {
		if (labelEl.__disposed) return;
		try {
			// pointer leave (silent)
			const sensorUid = labelEl.dataset && labelEl.dataset.sensorUid ? labelEl.dataset.sensorUid : (labelEl.dataset && labelEl.dataset.sensorId ? labelEl.dataset.sensorId : null);
			const e = new CustomEvent('label:leave', { detail: { sensorUid }, bubbles: true, composed: true });
			labelEl.dispatchEvent(e);
		} catch (err) { logger.warn && logger.warn('labels: pointerleave handler failed', err); }
	};

	const onUp = (ev) => {
		if (labelEl.__disposed) return;
		try {
			if (labelEl.classList && labelEl.classList.contains('hidden')) return;
			const newState = togglePersistent(labelEl);
			const sensorUid = labelEl.dataset && labelEl.dataset.sensorUid ? labelEl.dataset.sensorUid : (labelEl.dataset && labelEl.dataset.sensorId ? labelEl.dataset.sensorId : null);
			const e = new CustomEvent('label:persistent', { detail: { sensorUid, persistent: newState }, bubbles: true, composed: true });
			labelEl.dispatchEvent(e);
			ev.stopPropagation();
		} catch (err) { logger.warn && logger.warn('labels: pointerup handler failed', err); }
	};

	innerEl.addEventListener('pointerenter', onEnter);
	innerEl.addEventListener('pointerleave', onLeave);
	innerEl.addEventListener('pointerup', onUp);

	// store handlers and innerEl for disposal
	labelEl.__labelListeners = { innerEl, onEnter, onLeave, onUp };
};

export function togglePersistent(el) {
	if (!el) return false;
	const is = el.classList.contains('persistent');
	if (is) { el.classList.remove('persistent'); return false; }
	el.classList.add('persistent');
	return true;
}

export function setPersistent(el, on) {
	if (!el) return;
	if (on) el.classList.add('persistent'); else el.classList.remove('persistent');
}

export function isPersistent(el) { if (!el) return false; return el.classList.contains('persistent'); }

export function disposeLabel(el) {
	if (!el) return;
	// idempotent dispose: mark and safely remove listeners/dom
	try {
		if (el.__disposed) return;
		el.__disposed = true;
		// remove event listeners if present
		if (el.__labelListeners) {
			const { innerEl, onEnter, onLeave, onUp } = el.__labelListeners;
			try { innerEl && onEnter && innerEl.removeEventListener('pointerenter', onEnter); } catch (e) { }
			try { innerEl && onLeave && innerEl.removeEventListener('pointerleave', onLeave); } catch (e) { }
			try { innerEl && onUp && innerEl.removeEventListener('pointerup', onUp); } catch (e) { }
			try { delete el.__labelListeners; } catch (e) { }
		}
		try { if (el.parentElement) el.parentElement.removeChild(el); } catch (e) { }
	} catch (err) { logger.warn && logger.warn('labels: dispose failed', err); }
}

export function attachLabelTo(container, labelEl) {
	if (!container || !labelEl) return;
	try {
		// prefer DOM element for container
		if (container instanceof Element) {
			container.appendChild(labelEl);
			labelEl.__labelContainer = container;
		}
	} catch (err) { logger.warn && logger.warn('labels: attach failed', err); }
}

export default createSensorLabel;

// Update the label element with new sensor data. Supports either a single
// `{ value, unit }` payload or an object with `values` (array) for
// multi-value displays. This updates only the `.val` container to avoid
// rebuilding the whole DOM node.
export function updateSensorLabel(labelEl, sensor) {
	if (!labelEl) return;
	try {
		const inner = labelEl.querySelector('.label-inner') || labelEl;
		let valEl = inner.querySelector('.val');
		if (!valEl) {
			valEl = document.createElement('div');
			valEl.className = 'val';
			inner.appendChild(valEl);
		}

		// If sensor contains `values` array, render multiple rows
		if (sensor && Array.isArray(sensor.values) && sensor.values.length > 0) {
			const parts = sensor.values.map(v => {
				const lbl = v.label || '';
				const value = v.value !== undefined && v.value !== null ? String(v.value) : 'NaN';
				const unit = v.unit || '';
				return `<div class="val-row"><span class="val-label">${lbl}</span>: <span class="val-value">${value}</span> <span class="val-unit">${unit}</span></div>`;
			});
			valEl.innerHTML = parts.join('');
			return;
		}

		// Fallback: single value + unit
		valEl.textContent = 'no data';
	} catch (e) {
		// avoid throwing in rendering code
		try { console.warn && console.warn('updateSensorLabel failed', e); } catch (ee) { }
	}
}
